import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createCheckout, restorePurchases } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, KeyRound, Loader2, X, RotateCw, Calendar } from "lucide-react";
import { toast } from "sonner";
import { useEntitlements } from "@/lib/entitlements";

const YEARLY_LOOKUP = "umbra_companion_yearly";
const QUARTERLY_LOOKUP = "umbra_attending_quarterly";
const MONTHLY_PROMO_LOOKUP = "umbra_monthly_promo";

export default function Paywall({ section, onClose }) {
  const [busy, setBusy] = useState(null); // "yearly" | "quarterly" | "monthly" | "chapter" | "restore"
  const { refresh } = useEntitlements();

  const chapterLookup = section
    ? `chapter_unlock_${section.id.replace(/-/g, "_")}`
    : null;

  const startCheckout = async (lookupKey, kind) => {
    try {
      setBusy(kind);
      const { checkout_url } = await createCheckout(lookupKey);
      window.location.href = checkout_url;
    } catch (e) {
      console.error(e);
      toast.error("Could not open checkout. Please try again.");
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    setBusy("restore");
    try {
      const r = await restorePurchases();
      await refresh();
      const unlocked = (r?.chapters || []).length;
      if (r?.subscription) {
        toast.success("Your subscription has been restored.");
        if (onClose) onClose();
      } else if (unlocked > 0) {
        toast.success(`Restored ${unlocked} chapter${unlocked === 1 ? "" : "s"}.`);
        if (onClose) onClose();
      } else {
        toast.info("No previous purchases found for this account.");
      }
    } catch (e) {
      toast.error("Could not restore purchases right now.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
      data-testid="paywall"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {onClose && (
          <button
            onClick={onClose}
            data-testid="paywall-close"
            className="absolute top-5 right-5 text-bone-500 hover:text-bone-200 transition-colors z-10"
            aria-label="Close paywall"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <div className="p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-9 w-9 border border-terracotta/50 flex items-center justify-center">
              <Lock className="h-4 w-4 text-terracotta" />
            </div>
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">
              A threshold, gently held
            </p>
          </div>

          <h2 className="font-serif text-3xl md:text-4xl text-bone-100 leading-tight mb-4">
            You've reached the edge of the <em className="text-bone-300 italic">free chapter.</em>
          </h2>
          <p className="font-sans text-base text-bone-300 leading-relaxed max-w-xl mb-10">
            The next prompts move deeper into the work. Choose the door that
            feels right — a yearly companion, a quarterly season, a monthly rhythm,
            or a single chapter kept forever.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Yearly — best value */}
            <div className="border border-terracotta bg-terracotta/5 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-terracotta" />
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta">
                  Best value
                </p>
              </div>
              <h3 className="font-serif text-2xl text-bone-100 mb-2">
                Yearly
              </h3>
              <p className="font-serif text-4xl text-bone-100 mb-1">
                $89.99{" "}
                <span className="font-sans text-sm text-bone-400 tracking-wider">
                  / year
                </span>
              </p>
              <p className="font-sans text-xs text-bone-500 mb-6">
                $7.50/mo effective — 42% off Monthly.
              </p>
              <ul className="space-y-2 mb-8 font-sans text-sm text-bone-300 flex-1">
                <li>· Full workbook access all year</li>
                <li>· 12 ebooks per year</li>
                <li>· One free printed chapter book</li>
                <li>· Cancel any renewal</li>
              </ul>
              <Button
                onClick={() => startCheckout(YEARLY_LOOKUP, "yearly")}
                disabled={busy !== null}
                data-testid="paywall-yearly-btn"
                className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6 transition-colors w-full"
              >
                {busy === "yearly" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Calendar className="h-4 w-4 mr-2" />
                )}
                Save with yearly
              </Button>
            </div>

            {/* Quarterly — The Attending */}
            <div className="border border-obsidian-600 bg-obsidian-900 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-bone-400" />
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-400">
                  The Attending
                </p>
              </div>
              <h3 className="font-serif text-2xl text-bone-100 mb-2">
                Quarterly
              </h3>
              <p className="font-serif text-4xl text-bone-100 mb-1">
                $29.99{" "}
                <span className="font-sans text-sm text-bone-400 tracking-wider">
                  / 3 mo
                </span>
              </p>
              <p className="font-sans text-xs text-bone-500 mb-6">
                A gentler tempo — one season at a time.
              </p>
              <ul className="space-y-2 mb-8 font-sans text-sm text-bone-300 flex-1">
                <li>· Full workbook access</li>
                <li>· 3 ebooks per quarter</li>
                <li>· Break-even keepsake pricing</li>
                <li>· Cancel end of any quarter</li>
              </ul>
              <Button
                onClick={() => startCheckout(QUARTERLY_LOOKUP, "quarterly")}
                disabled={busy !== null}
                variant="outline"
                data-testid="paywall-quarterly-btn"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs py-6 transition-colors w-full"
              >
                {busy === "quarterly" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Begin the season
              </Button>
            </div>

            {/* Monthly Promo */}
            <div className="border border-obsidian-600 bg-obsidian-900 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-bone-400" />
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-400">
                  Monthly · promo
                </p>
              </div>
              <h3 className="font-serif text-2xl text-bone-100 mb-2">
                Monthly
              </h3>
              <p className="font-serif text-4xl text-bone-100 mb-1">
                $9.99{" "}
                <span className="font-sans text-sm text-bone-400 tracking-wider">
                  / month
                </span>
              </p>
              <p className="font-sans text-xs text-bone-500 mb-6">
                Founding rate · sign up through Dec 31, 2026.
              </p>
              <ul className="space-y-2 mb-8 font-sans text-sm text-bone-300 flex-1">
                <li>· Full workbook access</li>
                <li>· Unlimited Mirror reflections</li>
                <li>· Cancel anytime</li>
                <li>· Locked at $9.99 through Dec 31, 2027</li>
              </ul>
              <Button
                onClick={() => startCheckout(MONTHLY_PROMO_LOOKUP, "monthly")}
                disabled={busy !== null}
                variant="outline"
                data-testid="paywall-monthly-btn"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs py-6 transition-colors w-full"
              >
                {busy === "monthly" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Start monthly
              </Button>
            </div>

            {/* Chapter unlock */}
            <div className="border border-obsidian-600 bg-obsidian-900 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="h-4 w-4 text-bone-400" />
                <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-400">
                  One chapter
                </p>
              </div>
              <h3 className="font-serif text-2xl text-bone-100 mb-2">
                {section ? section.title : "This chapter"}
              </h3>
              <p className="font-serif text-4xl text-bone-100 mb-1">
                $10{" "}
                <span className="font-sans text-sm text-bone-400 tracking-wider">
                  · once
                </span>
              </p>
              <p className="font-sans text-xs text-bone-500 mb-6">
                Kept forever. No subscription.
              </p>
              <ul className="space-y-2 mb-8 font-sans text-sm text-bone-300 flex-1">
                <li>· This chapter's full prompt set</li>
                <li>· Save entries, moods, reflections</li>
                <li>· Chapter PDF export</li>
                <li>· Yours whether you subscribe or not</li>
              </ul>
              <Button
                onClick={() => chapterLookup && startCheckout(chapterLookup, "chapter")}
                disabled={busy !== null || !section}
                variant="outline"
                data-testid="paywall-chapter-btn"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs py-6 transition-colors w-full"
              >
                {busy === "chapter" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Unlock chapter
              </Button>
            </div>
          </div>

          <p className="mt-8 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-terracotta text-center">
            <Link
              to="/pricing"
              onClick={onClose}
              data-testid="paywall-see-pricing-link"
              className="hover:text-bone-100 transition-colors border-b border-transparent hover:border-bone-100 pb-0.5"
            >
              Compare all tiers side-by-side →
            </Link>
          </p>
          <div className="mt-4 flex items-center justify-center gap-6 flex-wrap">
            <p className="font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-500">
              Secured by Stripe · 7-day cancel window
            </p>
            <button
              onClick={handleRestore}
              disabled={busy !== null}
              data-testid="restore-purchases-btn"
              className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta transition-colors border-b border-transparent hover:border-terracotta pb-0.5"
            >
              {busy === "restore" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCw className="h-3 w-3" />
              )}
              Restore my purchases
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
